import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiPhone } from 'react-icons/fi';
import useIsMobile from '../utils/useIsMobile';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/#services' },
  { name: 'About Us', path: '/about' },
  { name: 'Contact Us', path: '/contact' },
  { name: 'Privacy Policy', path: '/privacy' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile(1100);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location]);

  // Close drawer when switching to desktop
  useEffect(() => { if (!isMobile) setIsOpen(false); }, [isMobile]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const isActive = (path) => {
    if (path === '/#services') return location.pathname === '/' && location.hash === '#services';
    return location.pathname === path;
  };

  const scrollToServices = () => {
    const el = document.getElementById('services');
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 72;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // Anchor links (/#services) reliably scroll to the section on every click;
  // page links scroll back to the top.
  const handleNavClick = (e, path) => {
    if (path.includes('#')) {
      if (location.pathname === '/') {
        e.preventDefault();
        scrollToServices();
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const navStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 40,
    transition: 'all 0.3s',
    background: scrolled ? 'rgba(255,255,255,0.97)' : '#fff',
    backdropFilter: scrolled ? 'blur(16px)' : 'none',
    boxShadow: scrolled
      ? '0 4px 24px rgba(15,23,42,0.1)'
      : '0 1px 0 rgba(0,0,0,0.06)',
  };

  return (
    <>
      <nav style={navStyle}>
        <div className="cc-container" style={{ padding: '0 var(--cc-px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 72 }}>

          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div style={{ lineHeight: 1 }}>
              <span style={{ display: 'block', color: '#0f172a', fontWeight: 800, fontSize: '1.35rem', letterSpacing: '-0.01em', fontFamily: "'Plus Jakarta Sans','DM Sans','Segoe UI',sans-serif" }}>InternetTV</span>
              <span style={{ display: 'block', color: '#2563eb', fontWeight: 800, fontSize: '0.9rem', letterSpacing: '0.14em', textTransform: 'uppercase', fontFamily: "'Plus Jakarta Sans','DM Sans','Segoe UI',sans-serif" }}>Service</span>
            </div>
          </Link>

          {/* Desktop links */}
          <div style={{ display: isMobile ? 'none' : 'flex', alignItems: 'center', gap: 2 }}>
            {navLinks.map(link => (
              <Link key={link.name} to={link.path} style={{
                position: 'relative', padding: '10px 14px', borderRadius: 8,
                textDecoration: 'none', fontSize: '0.95rem', fontWeight: 600,
                color: isActive(link.path) ? '#1e3a8a' : '#1f2937',
                background: isActive(link.path) ? '#eff6ff' : 'transparent',
                transition: 'color 0.18s, background 0.18s',
              }}
              onClick={(e) => handleNavClick(e, link.path)}
                onMouseEnter={e => { if (!isActive(link.path)) { e.currentTarget.style.color = '#1e3a8a'; e.currentTarget.style.background = '#f8fafc'; } }}
                onMouseLeave={e => { if (!isActive(link.path)) { e.currentTarget.style.color = '#1f2937'; e.currentTarget.style.background = 'transparent'; } }}
              >
                {link.name}
                {isActive(link.path) && (
                  <span style={{ position: 'absolute', bottom: 4, left: '14px', right: '14px', height: 2, background: '#2563eb', borderRadius: 2 }} />
                )}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <a href="tel:+18888802585" style={{
            display: isMobile ? 'none' : 'inline-flex', alignItems: 'center', gap: 8,
            background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
            color: '#fff', fontWeight: 700, fontSize: '1rem',
            padding: '12px 24px', borderRadius: 999, textDecoration: 'none',
            boxShadow: '0 4px 14px rgba(37,99,235,0.35)',
            transition: 'opacity 0.2s, transform 0.15s',
            flexShrink: 0,
          }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <FiPhone style={{ width: 15, height: 15 }} />
            +1 (888) 880-2585
          </a>

          {/* Hamburger — mobile only */}
          <button onClick={() => setIsOpen(!isOpen)} style={{
            display: isMobile ? 'flex' : 'none',
            alignItems: 'center', justifyContent: 'center',
            padding: 10, borderRadius: 10, border: 'none',
            background: isOpen ? '#f1f5f9' : 'transparent',
            cursor: 'pointer', color: '#475569',
            transition: 'background 0.2s',
          }}>
            {isOpen ? <FiX style={{ width: 22, height: 22 }} /> : <FiMenu style={{ width: 22, height: 22 }} />}
          </button>
        </div>
      </nav>

      {/* Mobile overlay */}
      {isOpen && (
        <div onClick={() => setIsOpen(false)} style={{
          position: 'fixed', inset: 0, zIndex: 30,
          background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)',
        }} />
      )}

      {/* Mobile drawer */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 50,
        width: 'min(85vw, 320px)', background: '#fff',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.15)',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Drawer header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--cc-px) 20px', borderBottom: '1px solid #f1f5f9' }}>
          <Link to="/" onClick={() => setIsOpen(false)} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#0f172a', fontWeight: 800, fontSize: '1.2rem', fontFamily: "'Plus Jakarta Sans','DM Sans','Segoe UI',sans-serif" }}>InternetTV Service</span>
          </Link>
          <button onClick={() => setIsOpen(false)} style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, padding: 8, cursor: 'pointer', color: '#64748b' }}>
            <FiX style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Drawer links */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {navLinks.map(link => (
            <Link key={link.name} to={link.path} onClick={(e) => { setIsOpen(false); handleNavClick(e, link.path); }} style={{
              display: 'flex', alignItems: 'center', padding: '14px var(--cc-px)',
              textDecoration: 'none', fontSize: '0.92rem', fontWeight: 600,
              color: isActive(link.path) ? '#1e3a8a' : '#334155',
              background: isActive(link.path) ? '#eff6ff' : 'transparent',
              borderLeft: `3px solid ${isActive(link.path) ? '#2563eb' : 'transparent'}`,
              transition: 'background 0.15s',
            }}>
              {link.name}
            </Link>
          ))}
        </div>

        {/* Drawer CTA */}
        <div style={{ padding: 'var(--cc-px) 20px', borderTop: '1px solid #f1f5f9' }}>
          <a href="tel:+18888802585" onClick={() => setIsOpen(false)} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            background: 'linear-gradient(135deg, #1e3a8a, #2563eb)',
            color: '#fff', fontWeight: 700, fontSize: '0.92rem',
            padding: '13px 20px', borderRadius: 999, textDecoration: 'none',
            boxShadow: '0 4px 14px rgba(37,99,235,0.3)',
          }}>
            <FiPhone style={{ width: 16, height: 16 }} />
            +1 (888) 880-2585
          </a>
        </div>
      </div>
    </>
  );
};

export default Navbar;
